/**
 * Cost provenance
 *
 * Every price in a TEA comes from somewhere: a citation in the cost master, a
 * value typed by the user, or a number baked into a template years ago by
 * someone who is no longer around to ask. The calculator treats all three the
 * same. This summarises which is which, weighted by how much of the total each
 * one carries, so a result can state its cost vintage instead of presenting an
 * unlabelled dollar figure.
 *
 * Weighted by cost, not by input count, because ten cited trace additives and
 * one uncited cathode powder is not a well-sourced analysis.
 *
 * Provenance travels on the input as `cost_source` / `cost_year` /
 * `cost_confidence`, written by whichever path set the price (the cost-master
 * picker, the ecoinvent lookup, or a template).
 */

export const CONFIDENCE = {
  SOURCED: 'sourced',       // a named citation, and the price the citation carries
  ESTIMATE: 'estimate',     // a cited estimate ("Steel market estimates (2024)")
  UNVERIFIED: 'unverified', // names a source, but not at the price that source carries
  UNSOURCED: 'unsourced',   // a price with no recorded origin
  LINKED: 'linked',         // rolled up from a sub-inventory template
};

// An unverified citation does not count as sourced. Every one of the five
// citations shipped in the built-in templates is unverified, so crediting them
// would make the headline share look four times better than it is.
const CITED = new Set([CONFIDENCE.SOURCED, CONFIDENCE.ESTIMATE]);

/**
 * Read provenance off an LCI input, inferring confidence when it was not
 * written explicitly (templates predating the cost master carry a bare
 * `cost_source`, or nothing at all).
 */
export function readCostProvenance(input) {
  const source = (input?.cost_source || '').trim();
  const year = (input?.cost_year || '').toString().trim() || yearFromSource(source);
  let confidence = (input?.cost_confidence || '').trim();
  if (!confidence) {
    if (!source) confidence = CONFIDENCE.UNSOURCED;
    else confidence = /estimate/i.test(source) ? CONFIDENCE.ESTIMATE : CONFIDENCE.SOURCED;
  }
  return { source, year, confidence };
}

// Every field that describes where an input's price came from.
export const COST_PROVENANCE_FIELDS = [
  'cost_ref', 'cost_source', 'cost_year', 'cost_confidence',
  'cost_url', 'cost_quote', 'cost_date', 'cost_note',
];

/**
 * The input with a new price and none of the old price's citation. A number
 * typed over a sourced price is not sourced, so whoever overwrites a price
 * goes through here and adds the new price's provenance, if it has any.
 */
export function withCost(input, costPerUnit, provenance = {}) {
  const next = { ...input };
  for (const f of COST_PROVENANCE_FIELDS) delete next[f];
  return { ...next, cost_per_unit: costPerUnit, cost_confidence: CONFIDENCE.UNSOURCED, ...provenance };
}

function yearFromSource(source) {
  const years = source.match(/\b(?:19|20)\d{2}\b/g);
  return years ? years[years.length - 1] : '';
}

/**
 * @param {Array} entries - { source, year, confidence, cost } per priced input
 * @returns {object} summary, or null when nothing in the project carries a cost
 */
export function summarizeCostProvenance(entries) {
  const priced = (entries || []).filter(e => e && Number.isFinite(e.cost) && e.cost > 0);
  if (priced.length === 0) return null;

  const bySource = new Map();
  let citedCost = 0, uncitedCost = 0, linkedCost = 0, unverifiedCost = 0;
  let citedInputs = 0, uncitedInputs = 0, linkedInputs = 0, unverifiedInputs = 0;

  for (const entry of priced) {
    if (entry.confidence === CONFIDENCE.LINKED) {
      linkedCost += entry.cost;
      linkedInputs += 1;
      continue;
    }
    if (CITED.has(entry.confidence) && entry.source) {
      citedCost += entry.cost;
      citedInputs += 1;
      const existing = bySource.get(entry.source) || { source: entry.source, year: entry.year, cost: 0, inputs: 0 };
      existing.cost += entry.cost;
      existing.inputs += 1;
      if (!existing.year && entry.year) existing.year = entry.year;
      bySource.set(entry.source, existing);
    } else {
      uncitedCost += entry.cost;
      uncitedInputs += 1;
      if (entry.confidence === CONFIDENCE.UNVERIFIED) {
        unverifiedCost += entry.cost;
        unverifiedInputs += 1;
      }
    }
  }

  const attributable = citedCost + uncitedCost;
  const years = [...bySource.values()].map(s => parseInt(s.year, 10)).filter(Number.isFinite);

  return {
    cited_cost: citedCost,
    uncited_cost: uncitedCost,
    linked_cost: linkedCost,
    // Share of the purchased-cost total that carries a citation. Sub-inventory
    // cost is excluded from the denominator: its provenance lives in the linked
    // template and is not resolved here.
    cited_share: attributable > 0 ? citedCost / attributable : 0,
    // Unverified is a subset of uncited, reported separately because it is a
    // different problem: a citation to chase down, not a price to find.
    unverified_cost: unverifiedCost,
    cited_inputs: citedInputs,
    uncited_inputs: uncitedInputs,
    unverified_inputs: unverifiedInputs,
    linked_inputs: linkedInputs,
    sources: [...bySource.values()].sort((a, b) => b.cost - a.cost),
    year_min: years.length ? Math.min(...years) : null,
    year_max: years.length ? Math.max(...years) : null,
  };
}

/**
 * A share that rounds to 0% while sources are listed reads as a contradiction,
 * so a non-zero sliver says "<1%".
 */
export function formatCitedShare(share) {
  if (share <= 0) return '0%';
  if (share < 0.005) return '<1%';
  return `${Math.round(share * 100)}%`;
}

/** One line a result panel or an export can print verbatim. */
export function describeCostProvenance(summary) {
  if (!summary) return '';
  const span = summary.year_min
    ? (summary.year_min === summary.year_max ? `${summary.year_min}` : `${summary.year_min}–${summary.year_max}`)
    : null;
  const parts = [`${formatCitedShare(summary.cited_share)} of cost from cited prices`];
  if (span) parts.push(`cost data ${span}`);
  if (summary.uncited_inputs > 0) {
    const plural = summary.uncited_inputs === 1 ? '' : 's';
    const unverified = summary.unverified_inputs > 0 ? `, ${summary.unverified_inputs} unverified` : '';
    parts.push(`${summary.uncited_inputs} uncited input${plural}${unverified}`);
  }
  if (summary.linked_inputs > 0) parts.push(`${summary.linked_inputs} from linked templates`);
  return parts.join(' · ');
}

export default { readCostProvenance, summarizeCostProvenance, describeCostProvenance, formatCitedShare, CONFIDENCE };
